import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Chip, Autocomplete, Grid, Checkbox, ListItemText,
  OutlinedInput, Switch, FormControlLabel, CircularProgress, Alert, Link
} from '@mui/material';
import { Search as SearchIcon, ImageSearch as LensIcon, AssignmentTurnedIn as AssignIcon, AutoAwesome as AiIcon } from '@mui/icons-material';
import { useGamesCache } from '../contexts/GamesContext';

const STOP_WORDS = new Set([
  // Grammar & prepositions
  'the', 'a', 'an', 'of', 'in', 'on', 'and', 'at', 'to', 'for', 'with', 'by', 'from', 'or', 'is', 'it', 'as',
  // Generic slot/casino buzzwords & mechanics
  'slot', 'slots', 'game', 'games', 'deluxe', 'live', 'edition', 'respin', 'respins', 'spin', 'spins',
  'jackpot', 'jackpots', 'megaways', 'hold', 'win', 'wins', 'cash', 'coin', 'coins', 'wild', 'wilds',
  'bonus', 'super', 'ultra', 'extra', 'extreme', 'plus', 'max', 'frenzy', 'blast', 'fever',
  'gold', 'golden', 'diamond', 'diamonds', 'fortune', 'lucky', 'luck', 'money', '777', 'classic',
  'big', 'mega', 'epic', 'vip', 'club', 'play', 'hit', 'drop', 'power', 'link', 'ways', 'lines', 'roll'
]);

const getSignificantTokens = (str) => {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 3 && !STOP_WORDS.has(t));
};

const findNearMatches = (targetTitle, targetGameId, allGames) => {
  if (!targetTitle || !allGames || allGames.length === 0) return [];
  
  const normTarget = targetTitle.toLowerCase().trim();
  const targetTokens = getSignificantTokens(normTarget);
  if (targetTokens.length === 0) return [];

  const scored = [];

  for (const game of allGames) {
    if (!game || !game.Name || game.GameID === targetGameId) continue;
    const sid = game.StudioID;
    if (!sid || sid === 'unknown' || sid === '47fd5aed-2876-471b-a326-70d6815600f2') continue;

    const normName = game.Name.toLowerCase().trim();
    const gameTokens = getSignificantTokens(normName);
    if (gameTokens.length === 0) continue;

    let score = 0;

    // 1. Direct Substring / Phrase Match
    const isExact = normName === normTarget;
    const isSubstring = normName.includes(normTarget) || normTarget.includes(normName);

    if (isExact) {
      score += 300;
    } else if (isSubstring) {
      const lengthRatio = Math.min(normName.length, normTarget.length) / Math.max(normName.length, normTarget.length);
      score += 150 + Math.round(lengthRatio * 80);
    }

    // 2. Token Matching & Completeness
    let overlapCount = 0;
    for (const t of targetTokens) {
      if (gameTokens.includes(t)) {
        overlapCount += 1;
      } else if (gameTokens.some(gt => gt.startsWith(t) || t.startsWith(gt))) {
        overlapCount += 0.5;
      }
    }

    if (overlapCount > 0) {
      const targetCoverage = overlapCount / targetTokens.length;
      const unionSize = targetTokens.length + gameTokens.length - overlapCount;
      const jaccard = overlapCount / (unionSize > 0 ? unionSize : 1);

      // Heavily reward complete / near-complete token overlap
      score += Math.round(targetCoverage * 120 + jaccard * 100);

      // Extra bonus for matching multiple meaningful words
      if (overlapCount >= 2) {
        score += 60;
      }

      // Bonus if the primary franchise keyword (first token) matches
      if (targetTokens[0] && gameTokens[0] && targetTokens[0] === gameTokens[0]) {
        score += 40;
      }

      // Filter out weak noise (e.g. single 1-word overlap on a 3+ word game without a substring match)
      if (!isSubstring && targetTokens.length >= 2 && overlapCount < 1.5 && targetCoverage < 0.5) {
        score = 0;
      }
    }

    // Minimum score threshold to qualify as a true "Near Match"
    if (score >= 70) {
      scored.push({ ...game, matchScore: score });
    }
  }

  // Order top-down by strongest match first
  scored.sort((a, b) => b.matchScore - a.matchScore);

  // Cut off at top 5
  return scored.slice(0, 5);
};

const SightingCropImage = ({ WebsitesDataID, BoundingBoxID, BoundingBox, onLoad }) => {
  const [cropSrc, setCropSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!WebsitesDataID || !BoundingBox) {
      setError(true);
      setLoading(false);
      return;
    }

    const loadCrop = async () => {
      try {
        const websitesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/screenshots/${WebsitesDataID}`,
          { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } }
        );
        const imageLocation = websitesRes.data.ImageLocation;
        if (!imageLocation) throw new Error('No ImageLocation found');

        const cropRes = await axios.post(
          `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/game-management/upload-crop`,
          {
            image_location: imageLocation,
            bounding_box: BoundingBox
          },
          {
            headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY }
          }
        );

        if (active) {
          setCropSrc(cropRes.data.url);
          setLoading(false);
          if (onLoad) onLoad(cropRes.data.url);
        }
      } catch (err) {
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadCrop();
    return () => { active = false; };
  }, [WebsitesDataID, BoundingBoxID, BoundingBox]);

  if (error) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e0e0e0', borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">No Image</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <img 
      src={cropSrc} 
      alt="Crop Sighting" 
      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
    />
  );
};

const StudioFinder = () => {
  const API_URL = process.env.REACT_APP_API_GAMES_STUDIOS_URL;
  const API_KEY = process.env.REACT_APP_API_GAMES_STUDIOS_KEY;

  const { games, fetchGames, studios, fetchStudios } = useGamesCache();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [ignoreTableGames, setIgnoreTableGames] = useState(true);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedStudio, setSelectedStudio] = useState(null);
  
  const [nearMatches, setNearMatches] = useState([]);
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiSources, setAiSources] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [recentTiles, setRecentTiles] = useState([]);
  const [seenBrands, setSeenBrands] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [lensLoading, setLensLoading] = useState(false);
  
  const [websitesCatalog, setWebsitesCatalog] = useState({});
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    fetchGames();
    fetchStudios();
    axios.get(`${process.env.REACT_APP_API_URL}/websites`, { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } })
      .then(res => {
          const cat = {};
          const tagsSet = new Set();
          res.data.forEach(w => {
              cat[w.WebsitesDBID] = w;
              if (w.Tags && Array.isArray(w.Tags)) {
                  w.Tags.forEach(t => tagsSet.add(t.toLowerCase()));
              }
          });
          setWebsitesCatalog(cat);
          setAvailableTags(Array.from(tagsSet).sort());
      }).catch(err => console.error('Failed to load websites', err));
  }, [fetchGames, fetchStudios]);

  useEffect(() => {
    fetchCandidates();
  }, [tags]);

  const fetchGameSummary = async (gameId) => {
    setSummaryLoading(true);
    setRecentTiles([]);
    setSeenBrands([]);
    try {
      const res = await axios.get(`${API_URL}/games/${gameId}/summary?minWidth=30&minHeight=30`, {
        headers: { 'x-api-key': API_KEY }
      });
      setRecentTiles(res.data.RecentTiles || []);
      setSeenBrands(res.data.Brands || []);
    } catch (e) {
      console.error("Failed to fetch game summary", e);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const query = tags.length > 0 ? `?tags=${tags.join(',')}` : '';
      const res = await axios.get(`${API_URL}/game-management/unknown-candidates${query}`, {
        headers: { 'x-api-key': API_KEY }
      });
      setCandidates(res.data.candidates || []);
      setCurrentIndex(0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };



  const handleAiDetective = async (gameName) => {
    setAiLoading(true);
    setAiAnswer('');
    setAiSources([]);
    try {
      const res = await axios.post(`${API_URL}/game-management/ai-detective`, { game_name: gameName }, {
        headers: { 'x-api-key': API_KEY }
      });
      setAiAnswer(res.data.answer || 'No answer found.');
      setAiSources(res.data.sources || []);
    } catch (e) {
      setAiAnswer('Error calling AI Detective.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAssignStudio = async () => {
    if (!selectedStudio || !filteredCandidates[currentIndex]) return;
    setAssignLoading(true);
    try {
      const game = filteredCandidates[currentIndex];
      await axios.patch(`${API_URL}/games`, {
        GameID: game.GameID,
        StudioID: selectedStudio.StudioID
      }, { headers: { 'x-api-key': API_KEY } });
      
      setCandidates(prev => prev.filter(c => c.GameID !== game.GameID));
      setSelectedStudio(null);
      setNearMatches([]);
      setAiAnswer('');
      setAiSources([]);
      setSeenBrands([]);
    } catch (e) {
      console.error("Failed to assign studio", e);
      alert("Failed to assign studio");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleTagChange = (event) => {
    const { target: { value } } = event;
    setTags(typeof value === 'string' ? value.split(',') : value);
  };

  const isTableGame = (name) => {
    const lower = (name || '').toLowerCase();
    return lower.includes('roulette') || lower.includes('blackjack');
  };

  const getStudioName = (studioId) => {
    const s = studios.find(st => st.StudioID === studioId);
    return s ? s.Name : studioId;
  };

  const filteredCandidates = candidates.filter(c => {
    if (ignoreTableGames && isTableGame(c.Name)) return false;
    return true;
  });

  const currentGame = filteredCandidates[currentIndex];

  useEffect(() => {
    if (currentGame) {
      const matches = findNearMatches(currentGame.Name, currentGame.GameID, games);
      setNearMatches(matches);
      fetchGameSummary(currentGame.GameID);
      setAiAnswer('');
      setAiSources([]);
      setSeenBrands([]);
    }
  }, [currentGame, games]);

  const openGoogleLens = async () => {
    if (recentTiles.length === 0) return;
    setLensLoading(true);
    try {
      const tile = recentTiles[0];
      
      // 1. Fetch screenshot metadata to get the high-res S3 ImageLocation
      const websitesRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/screenshots/${tile.WebsitesDataID}`,
        { headers: { 'x-api-key': process.env.REACT_APP_API_KEY } }
      );
      const imageLocation = websitesRes.data.ImageLocation;
      if (!imageLocation) throw new Error('No ImageLocation found for screenshot');

      // 2. POST S3 ImageLocation and BoundingBox to native backend cropping endpoint
      const res = await axios.post(`${API_URL}/game-management/upload-crop`, 
        { 
          image_location: imageLocation,
          bounding_box: tile.BoundingBox
        },
        { headers: { 'x-api-key': API_KEY } }
      );
      
      const presignedUrl = res.data.url;
      window.open(`https://lens.google.com/uploadbyurl?url=${encodeURIComponent(presignedUrl)}`, '_blank');
    } catch (e) {
      console.error("Failed to perform native crop for Google Lens", e);
      alert("Failed to perform native high-res crop for Google Lens search.");
    } finally {
      setLensLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>Studio Finder Detective</Typography>
      
      <Grid container spacing={3} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent>
              <Typography variant="h6">Queue Filters</Typography>
              <FormControl sx={{ m: 1, width: '100%' }}>
                <InputLabel>Tags Filter</InputLabel>
                <Select
                  multiple
                  value={tags}
                  onChange={handleTagChange}
                  input={<OutlinedInput label="Tags Filter" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {availableTags.map((name) => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={tags.indexOf(name) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={ignoreTableGames} onChange={(e) => setIgnoreTableGames(e.target.checked)} />}
                label="Ignore Roulette/Blackjack"
              />
            </CardContent>
            
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, borderTop: '1px solid #eee' }}>
              {loading ? <CircularProgress /> : (
                <Typography variant="subtitle1" gutterBottom>
                  {filteredCandidates.length} Games Remaining
                </Typography>
              )}
              {filteredCandidates.map((c, idx) => (
                <Box 
                  key={c.GameID} 
                  sx={{ 
                    p: 1.5, mb: 1, borderRadius: 1, 
                    cursor: 'pointer',
                    backgroundColor: idx === currentIndex ? '#e3f2fd' : '#fafafa',
                    border: idx === currentIndex ? '1px solid #2196f3' : '1px solid #ddd'
                  }}
                  onClick={() => setCurrentIndex(idx)}
                >
                  <Typography variant="body2" fontWeight="bold">{c.Name}</Typography>
                  <Typography variant="caption" color="text.secondary">Score: {c.CalculatedScore || c.TotalScore}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', pb: 10, px: 2 }}>
          {currentGame ? (
            <>
              <Card sx={{ mb: 2, flexShrink: 0 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" fontWeight="bold">{currentGame.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">ID: {currentGame.GameID} | Score: {currentGame.CalculatedScore || currentGame.TotalScore}</Typography>
                      {seenBrands.length > 0 && (
                        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Seen on:</Typography>
                          {seenBrands.map(b => (
                            <Chip 
                              key={b.BrandID} 
                              size="small" 
                              label={b.BrandName} 
                              variant="outlined" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem', 
                                borderColor: 'primary.light', 
                                color: 'primary.main',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                              }} 
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Button variant="outlined" startIcon={<LensIcon />} onClick={openGoogleLens} sx={{ mr: 1 }}>
                        Google Lens
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                    {summaryLoading ? (
                      <CircularProgress size={24} />
                    ) : recentTiles.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No visual crops captured in database.</Typography>
                    ) : (
                      recentTiles.map((tile, idx) => {
                        const dateStr = tile.Timestamp ? tile.Timestamp.split('T')[0] : '';
                        const websiteParam = tile.WebsiteID ? `&websiteId=${tile.WebsiteID}` : '';
                        const deepLinkUrl = `/time-machine?scanId=${tile.WebsitesDataID}${websiteParam}${dateStr ? `&date=${dateStr}` : ''}`;
                        const websiteName = websitesCatalog[tile.WebsiteID]?.WebsiteName || tile.WebsiteID || 'Unknown Site';
                        return (
                          <Box key={tile.BoundingBoxID || idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '200px' }}>
                            <Box
                              component="a"
                              href={deepLinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                width: '200px',
                                height: '140px',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                cursor: 'pointer',
                                '&:hover': {
                                  transform: 'scale(1.03)',
                                  boxShadow: 3,
                                  borderColor: 'primary.main'
                                }
                              }}
                            >
                              <SightingCropImage
                                WebsitesDataID={tile.WebsitesDataID}
                                BoundingBoxID={tile.BoundingBoxID}
                                BoundingBox={tile.BoundingBox}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', fontWeight: 'bold' }}>
                              {websiteName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                              {dateStr}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', backgroundColor: '#f9fbe7' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                          <AiIcon color="primary" /> AI Detective
                        </Typography>
                        <Button variant="contained" color="primary" size="small" onClick={() => handleAiDetective(currentGame.Name)} disabled={aiLoading}>
                          Ask Gemini
                        </Button>
                      </Box>
                      {aiLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {aiAnswer || "Click 'Ask Gemini' to search the web for this studio."}
                          </Typography>
                          {aiSources.length > 0 && (
                            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                                Sources & Evidence:
                              </Typography>
                              <Box display="flex" flexDirection="column" gap={0.75}>
                                {aiSources.map((src, i) => (
                                  <Link 
                                    key={i} 
                                    href={src.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    variant="caption"
                                    sx={{ 
                                      color: '#1976d2', 
                                      textDecoration: 'none', 
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      '&:hover': { textDecoration: 'underline' } 
                                    }}
                                  >
                                    • {src.title}
                                  </Link>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" mb={2}>Near Matches</Typography>
                      {nearMatches.filter(m => 
                        m.StudioID !== '47fd5aed-2876-471b-a326-70d6815600f2' && 
                        m.StudioID !== 'unknown' && 
                        m.GameID !== currentGame.GameID
                      ).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No near matches found.</Typography>
                      ) : (
                        <Box>
                          {nearMatches
                            .filter(m => 
                              m.StudioID !== '47fd5aed-2876-471b-a326-70d6815600f2' && 
                              m.StudioID !== 'unknown' && 
                              m.GameID !== currentGame.GameID
                            )
                            .map(m => (
                              <Box key={m.GameID} sx={{ display: 'flex', flexDirection: 'column', mb: 1.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #9c27b0' }}>
                                <Typography variant="body2" fontWeight="bold">{m.Name}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Studio: {getStudioName(m.StudioID)}
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ mt: 'auto', borderTop: '4px solid #2196f3', flexShrink: 0 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Autocomplete
                    options={studios}
                    getOptionLabel={(option) => `${option.Name} (${option.StudioID})`}
                    sx={{ width: 400 }}
                    value={selectedStudio}
                    onChange={(e, newValue) => setSelectedStudio(newValue)}
                    renderInput={(params) => <TextField {...params} label="Select Studio to Assign" variant="outlined" />}
                  />
                  <Button 
                    variant="contained" 
                    color="success" 
                    size="large" 
                    startIcon={<AssignIcon />}
                    onClick={handleAssignStudio}
                    disabled={!selectedStudio || assignLoading}
                  >
                    {assignLoading ? <CircularProgress size={24} /> : "Assign Studio"}
                  </Button>
                  <Button variant="text" onClick={() => setCurrentIndex(i => Math.min(filteredCandidates.length - 1, i + 1))}>
                    Skip
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 5 }}>No unknown games found for the selected tags.</Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudioFinder;
